import { eachDayOfInterval, format } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';

import { handleLoadByViewMode } from '../../helpers';
import { useMuiPickersLocale } from '../../hooks';
import {
  reportClosingCollectionsSelector,
  reportClosingCurrentSelector,
  reportClosingFilterCountrySelector,
  reportClosingFilterEndBusinessDateSelector,
  reportClosingFilterStartBusinessDateSelector,
  reportClosingFilterViewModeSelector,
  reportClosingSalesSelector,
} from '../../state';
import { ClosingDetailGroup } from '../../types';
import handlerFactory, { ClosingDataGridContext } from './handlerFactory';
import { MissingDialogPayload } from './types';

const getDatesHeaders = (start: Date | null, end: Date | null, locale: Locale) => {
  if (!start || !end || start > end) return [];

  return eachDayOfInterval({
    start,
    end,
  })
    .map(i => format(i, 'dd MMM', { locale }))
    .reverse();
};

interface ClosingDataGridProps {
  onOpenModal?: (payload: MissingDialogPayload) => void;
}

const DataGridConfig = ({ onOpenModal }: ClosingDataGridProps) => {
  const { handler } = useDataGridFlexSales<ClosingDetailGroup, ClosingDataGridContext>();

  const { t } = useTranslation();
  const title = t('missing-closing-detail.title');
  const locale = useMuiPickersLocale();
  const salesDetail = useSelector(reportClosingSalesSelector);
  const collectionsDetail = useSelector(reportClosingCollectionsSelector);
  const viewMode = useSelector(reportClosingFilterViewModeSelector);
  const load = handleLoadByViewMode(viewMode, salesDetail, collectionsDetail);
  const dateFrom = useSelector(reportClosingFilterStartBusinessDateSelector).accept;
  const dateTo = useSelector(reportClosingFilterEndBusinessDateSelector).accept;
  const countries = useSelector(reportClosingFilterCountrySelector);
  const data = useSelector(reportClosingCurrentSelector);
  const headerDates = useMemo(
    () => getDatesHeaders(dateFrom, dateTo, locale),
    [dateFrom, dateTo, locale]
  );

  const gridContext = useMemo(
    () => ({
      t,
      viewMode,
      countries,
      onOpenModal,
    }),
    [t, countries, viewMode, onOpenModal]
  );

  useEffect(() => {
    handler.updateColumnIterator('dates', headerDates);
  }, [handler, headerDates]);

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title,
    loading: load[0],
  });

  return null;
};

const ClosingDataGrid = ({ onOpenModal }: ClosingDataGridProps) => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig onOpenModal={onOpenModal} />
  </DataGridFlexSales>
);

export default ClosingDataGrid;
